import React, { useState, useEffect, useMemo } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, waitForAuthReady } from '../../../../utils/firebase';
import { useAuth } from '../../../../contexts/AuthContext';
import Card from '../../../../components/ui/Card';
import Button from '../../../../components/ui/Button';
import InputField from '../../../../components/ui/InputField';
import ToggleSwitch from '../../../../components/ui/ToggleSwitch';
import {
  computeSalaryDeduction,
  parseSalaryTaxSettings,
  type SalaryTaxLineConfig,
  type SalaryTaxLineMode,
  type SalaryTaxSettings,
} from '../../../../utils/salaryTax';

const DOC_ID = 'default';

const pxToRem = (value: number) => `${value / 16}rem`;

const newLineId = () => `line-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

interface SalaryTaxSettingsPanelProps {
  /** 저장 성공 시 호출 — 승인 목록의 실수령 예상 등이 같은 설정을 다시 불러오도록 함 */
  onSaved?: () => void;
}

const SalaryTaxSettingsPanel: React.FC<SalaryTaxSettingsPanelProps> = ({ onSaved }) => {
  const { currentUser } = useAuth();
  const teacher = currentUser as { schoolId?: string; classId?: string } | null;
  const schoolId = teacher?.schoolId;
  const classId = teacher?.classId;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [lines, setLines] = useState<SalaryTaxLineConfig[]>([]);
  const [previewGross, setPreviewGross] = useState(1000);

  const settingsForPreview: SalaryTaxSettings = useMemo(
    () => ({ enabled, lines }),
    [enabled, lines]
  );

  const preview = useMemo(
    () => computeSalaryDeduction(previewGross, settingsForPreview),
    [previewGross, settingsForPreview]
  );

  useEffect(() => {
    if (!schoolId || !classId) {
      setLoading(false);
      return;
    }
    const load = async () => {
      setError(null);
      try {
        await waitForAuthReady();
        const ref = doc(db, `schools/${schoolId}/classes/${classId}/salaryTaxSettings`, DOC_ID);
        const snap = await getDoc(ref);
        const parsed = parseSalaryTaxSettings(snap.data() as Record<string, unknown> | undefined);
        if (!snap.exists()) {
          setEnabled(false);
          setLines([
            {
              id: newLineId(),
              label: '소득세',
              mode: 'rate',
              ratePercent: 3,
              fixedAmount: 0,
            },
          ]);
        } else {
          setEnabled(parsed.enabled);
          setLines(parsed.lines);
        }
      } catch (e) {
        console.error(e);
        setError('급여 세금 설정을 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [schoolId, classId]);

  const handleSave = async () => {
    if (!schoolId || !classId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await waitForAuthReady();
      const ref = doc(db, `schools/${schoolId}/classes/${classId}/salaryTaxSettings`, DOC_ID);
      await setDoc(
        ref,
        {
          enabled,
          lines: lines.map((l) => ({
            id: l.id || newLineId(),
            label: l.label.trim(),
            mode: l.mode === 'fixed' ? 'fixed' : 'rate',
            ratePercent: Math.max(0, Number(l.ratePercent) || 0),
            fixedAmount: Math.max(0, Math.floor(Number(l.fixedAmount) || 0)),
          })),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setSuccess('저장했습니다.');
      onSaved?.();
    } catch (e) {
      console.error(e);
      setError('저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      { id: newLineId(), label: '', mode: 'rate', ratePercent: 0, fixedAmount: 0 },
    ]);
  };

  const removeLine = (id: string) => {
    setLines((prev) => prev.filter((l) => l.id !== id));
  };

  const updateLine = (id: string, patch: Partial<SalaryTaxLineConfig>) => {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  if (!schoolId || !classId) {
    return null;
  }

  if (loading) {
    return (
      <div style={{ fontSize: pxToRem(14), color: 'var(--Color-text-normal)', fontFamily: 'var(--font-family-primary)' }}>
        급여 세금 설정 불러오는 중…
      </div>
    );
  }

  return (
    <Card
      width="100%"
      padding={pxToRem(20)}
      borderRadius={pxToRem(16)}
      background="var(--Color-background-normal)"
      gap={pxToRem(16)}
    >
      <div
        style={{
          fontSize: pxToRem(18),
          fontWeight: 600,
          color: 'var(--Color-text-strong)',
          fontFamily: 'var(--font-family-primary)',
        }}
      >
        급여 세금(공제) 설정
      </div>
      <div style={{ fontSize: pxToRem(13), color: 'var(--Color-text-normal)', lineHeight: 1.5 }}>
        역할 급여(총액)에 대해 항목마다 <strong>세율(%)</strong> 또는 <strong>고정 포인트</strong>로 공제합니다. 위에서부터 순서대로 적용되며, 세율은 항상 총 급여 기준으로 계산되고 고정액은 남은 급여 한도 안에서만 공제됩니다. 실제 지급은 공제 후 포인트이며, 학생 포인트 화면에서 명세서로 확인할 수 있습니다.
      </div>

      {error && (
        <div
          style={{
            padding: pxToRem(12),
            background: 'var(--Color-status-negative-bg, #fee)',
            color: 'var(--Color-status-negative, #c33)',
            borderRadius: pxToRem(8),
            fontSize: pxToRem(14),
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: pxToRem(12),
            background: 'var(--Color-status-positive-bg, #e8f5e9)',
            color: 'var(--Color-status-positive, #2e7d32)',
            borderRadius: pxToRem(8),
            fontSize: pxToRem(14),
          }}
        >
          {success}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: pxToRem(12) }}>
        <span style={{ fontSize: pxToRem(14), fontWeight: 500 }}>공제 사용</span>
        <ToggleSwitch checked={enabled} onChange={setEnabled} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: pxToRem(12) }}>
        {lines.map((line) => (
          <div
            key={line.id}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: pxToRem(8),
              alignItems: 'flex-end',
            }}
          >
            <div style={{ flex: '1 1 140px', minWidth: 0 }}>
              <InputField
                label="항목 이름"
                value={line.label}
                onChange={(v) => updateLine(line.id, { label: v })}
                placeholder="예: 소득세"
                size="medium"
              />
            </div>
            <div style={{ width: pxToRem(130) }}>
              <div
                style={{
                  fontSize: pxToRem(12),
                  fontWeight: 500,
                  color: 'var(--Color-text-assistive, #989898)',
                  marginBottom: pxToRem(6),
                  fontFamily: 'var(--font-family-primary)',
                }}
              >
                유형
              </div>
              <select
                value={line.mode === 'fixed' ? 'fixed' : 'rate'}
                onChange={(e) =>
                  updateLine(line.id, { mode: e.target.value as SalaryTaxLineMode })
                }
                style={{
                  width: '100%',
                  height: pxToRem(44),
                  padding: `0 ${pxToRem(12)}`,
                  borderRadius: pxToRem(8),
                  border: '1px solid var(--Color-line-normal, #F0F0F0)',
                  fontSize: pxToRem(14),
                  fontFamily: 'var(--font-family-primary)',
                  background: 'var(--Color-background-normal, #fff)',
                  color: 'var(--Color-text-strong)',
                }}
              >
                <option value="rate">세율 (%)</option>
                <option value="fixed">고정 (P)</option>
              </select>
            </div>
            {line.mode === 'fixed' ? (
              <div style={{ width: pxToRem(120) }}>
                <InputField
                  label="고정 공제(P)"
                  value={String(line.fixedAmount ?? 0)}
                  onChange={(v) =>
                    updateLine(line.id, {
                      fixedAmount: Math.max(0, Math.floor(Number(v.replace(/[^0-9]/g, '')) || 0)),
                    })
                  }
                  placeholder="0"
                  size="medium"
                />
              </div>
            ) : (
              <div style={{ width: pxToRem(120) }}>
                <InputField
                  label="세율(%)"
                  value={String(line.ratePercent)}
                  onChange={(v) =>
                    updateLine(line.id, { ratePercent: Number(v.replace(/[^0-9.]/g, '')) || 0 })
                  }
                  placeholder="0"
                  size="medium"
                />
              </div>
            )}
            <Button variant="outline" size="small" onClick={() => removeLine(line.id)}>
              삭제
            </Button>
          </div>
        ))}
        <Button variant="secondary" size="small" onClick={addLine} width="fit-content">
          항목 추가
        </Button>
      </div>

      <div
        style={{
          borderTop: '1px solid var(--Color-line-normal)',
          paddingTop: pxToRem(16),
          display: 'flex',
          flexDirection: 'column',
          gap: pxToRem(8),
        }}
      >
        <div style={{ fontSize: pxToRem(14), fontWeight: 600 }}>미리보기 (총 급여 포인트)</div>
        <div style={{ maxWidth: pxToRem(200) }}>
          <InputField
            label="총액"
            value={String(previewGross)}
            onChange={(v) => setPreviewGross(Math.max(0, Math.floor(Number(v.replace(/[^0-9.-]/g, '')) || 0)))}
            size="medium"
          />
        </div>
        <div style={{ fontSize: pxToRem(13), color: 'var(--Color-text-normal)' }}>
          총 급여 <strong>{preview.gross.toLocaleString('ko-KR')}</strong> P
        </div>
        {preview.taxLines.map((t, idx) => (
          <div key={`${t.label}-${idx}-${t.amount}`} style={{ fontSize: pxToRem(13) }}>
            └ {t.label}: −{t.amount.toLocaleString('ko-KR')} P
          </div>
        ))}
        <div style={{ fontSize: pxToRem(14), fontWeight: 600, marginTop: pxToRem(4) }}>
          실수령(지급) {preview.net.toLocaleString('ko-KR')} P
        </div>
      </div>

      <Button variant="primary" size="medium" onClick={handleSave} disabled={saving}>
        {saving ? '저장 중…' : '설정 저장'}
      </Button>
    </Card>
  );
};

export default SalaryTaxSettingsPanel;
